import _ from 'lodash';
import { ListFilesResponse } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/responses/list-files-response';
import { FileType } from '@wix/media-platform-js-client/dist/mjs/src/types/media-platform/media-platform';

import { TableSorting } from '../../redux/sorted-columns/sorted-columns.reducer';
import { Project, ProjectInOrganizationId } from '../../types/entities';
import { MaybeUndef } from '../../types/system';
import { download } from '../../utils/file/file';
import { getFileName } from '../../utils/file/file-path';
import { join } from '../../utils/path/path';
import { mediaPlatformProvider } from '../media-platform-provider';
import { SignedDownloadUrlRequest } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/requests/signed-download-url-request';
import { transport } from '../transport';
import { BASE_PATH, PROJECT_PATH } from './config';
import { ImageTokenResponse } from '../../types/media-assets';

export interface PathSpecificParams {
  path: string;
  organizationId: string;
  project: Project;
}

export interface GetFilesListMetaParams {
  sort?: MaybeUndef<TableSorting>;
  pageSize: number;
  nextPageToken?: string;
  type?: FileType;
}

const getListFiles = (
  { path, organizationId, project }: PathSpecificParams,
  { sort, pageSize, type, nextPageToken }: GetFilesListMetaParams,
): Promise<ListFilesResponse> => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;

  const sorting = sort
    ? {
        orderBy: sort.columnName,
        orderDirection: sort.direction,
      }
    : undefined;

  const correctPath = _.startsWith(path, '/') ? path : `/${path}`;

  return fileManager.listFiles(correctPath, {
    pageSize,
    nextPageToken,
    ...sorting,
    type,
  });
};

const createFolder = ({
  path,
  organizationId,
  project,
}: PathSpecificParams) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;

  return fileManager.createFolder({ path });
};

const deleteFileByPath = (
  { organizationId, project }: ProjectInOrganizationId,
  path: string,
) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;
  return fileManager.deleteFileByPath(path);
};

const deleteFileById = (
  { organizationId, project }: ProjectInOrganizationId,
  id: string,
) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;
  return fileManager.deleteFileById(id);
};

const updateFileACL = ({ acl, id, organizationId, project }) => {
  return mediaPlatformProvider
    .getSDKInstance(organizationId, project)
    .fileManager.updateFileACL({ acl, id });
};

const downloadFile = ({
  path,
  organizationId,
  project,
}: PathSpecificParams) => {
  const mediaPlatform = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  );
  const fileName = getFileName(path);
  const downloadUrlRequest: SignedDownloadUrlRequest = {
    saveAs: fileName,
  };

  return mediaPlatform
    .getSignedUrl(path, downloadUrlRequest)
    .then(({ downloadUrl }) => {
      download(`${mediaPlatform.fileManager.baseUrl}${downloadUrl}`);
    });
};

const uploadFile = ({
  path,
  file,
  organizationId,
  project,
}: PathSpecificParams & { file: File }) => {
  const fullPath = join(path, file.name);
  const mediaPlatform = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  );
  const MP = mediaPlatformProvider.getMediaPlatform();
  const uploadJob = new MP.upload.UploadJob({ file, path: fullPath });

  mediaPlatform.fileManager.queueFileUpload &&
    mediaPlatform.fileManager.queueFileUpload(uploadJob);

  return uploadJob;
};

// TODO: This is a hack/hotfix which adds missing mediaType filed to metadata. mediaType must be added at SDK level
const addMissingMediaType = (metadata) => {
  const type = metadata.fileDescriptor.mimeType.split('/')[0].toLowerCase();

  if (type) {
    metadata.mediaType = type;
  }

  return metadata;
};

const getImageToken = async ({
  organizationId,
  project,
  path,
}): Promise<string> => {
  const url = `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}/assets_manager/image_token`;

  const { data } = await transport.post<ImageTokenResponse>(url, {
    path,
  });
  return data.token;
};

const getFileMetadataById = ({ id, organizationId, project }) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;

  return fileManager.getFileMetadataById(id).then(addMissingMediaType);
};

const getFileByPath = ({ path, organizationId, project }) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;

  return fileManager.getFile(path);
};

const getFileById = ({ id, organizationId, project }) => {
  const fileManager = mediaPlatformProvider.getSDKInstance(
    organizationId,
    project,
  ).fileManager;

  return fileManager.getFileMetadataById(id);
};

export const mediaAssetsService = {
  getListFiles,
  getFileMetadataById,

  createFolder,

  deleteFileByPath,
  deleteFileById,
  getFileByPath,
  getFileById,
  getImageToken,
  downloadFile,
  uploadFile,

  updateFileACL,
};
